import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, message,
  Typography, Row, Col, Select, DatePicker, Popconfirm, Input, Descriptions, Drawer
} from 'antd';
import {
  SearchOutlined, FileTextOutlined, CheckCircleOutlined,
  RollbackOutlined, ReloadOutlined, EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import superAdminApi, { AdminInvoice, AdminCompany, PaginationParams } from '../../api/superAdminApi';
import { useIsMobile } from '../../../../utils/responsiveUtils';
import MobileDataList from '../../../../components/mobile/MobileDataList';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const InvoiceManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterCompany, setFilterCompany] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[any, any] | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<AdminInvoice | null>(null);

  const fetchInvoices = useCallback(async (params: PaginationParams = {}) => {
    try {
      setLoading(true);
      const response = await superAdminApi.getInvoices({
        page: params.page || pagination.page,
        limit: params.limit || pagination.limit,
        status: filterStatus,
        companyId: filterCompany,
        startDate: dateRange?.[0]?.toISOString(),
        endDate: dateRange?.[1]?.toISOString(),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (response.success) {
        setInvoices(response.data);
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total
        });
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCompany, dateRange, pagination.page, pagination.limit]);

  const fetchCompanies = async () => {
    try {
      const response = await superAdminApi.getCompanies({ limit: 100 });
      if (response.success) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch companies', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchCompanies();
  }, []);

  const handleTableChange = (paginationConfig: any) => {
    fetchInvoices({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize
    });
  };

  const handleViewDetails = (record: AdminInvoice) => {
    setSelectedInvoice(record);
    setDetailModalVisible(true);
  };

  const handleMarkPaid = async (record: AdminInvoice) => {
    try {
      await superAdminApi.markInvoicePaid(record.id);
      message.success('Invoice marked as paid');
      fetchInvoices();
    } catch (error: any) {
      message.error(error.message || 'Failed to mark invoice as paid');
    }
  };

  const handleRefund = async (record: AdminInvoice) => {
    try {
      await superAdminApi.refundInvoice(record.id);
      message.success('Invoice refunded');
      fetchInvoices();
    } catch (error: any) {
      message.error(error.message || 'Failed to refund invoice');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID': return 'green';
      case 'PENDING': return 'orange';
      case 'OVERDUE': return 'red';
      case 'FAILED': return 'red';
      case 'REFUNDED': return 'purple';
      default: return 'default';
    }
  };

  const columns: ColumnsType<AdminInvoice> = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 140,
      render: (no) => <Text strong>{no}</Text>
    },
    {
      title: 'Company',
      key: 'company',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.companyName}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.companyCode}</Text>
        </Space>
      )
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 120,
      render: (plan) => plan ? <Tag color="blue">{plan}</Tag> : '-'
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>${Number(record.total).toLocaleString()}</Text>
          {Number(record.tax) > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Tax: ${Number(record.tax).toLocaleString()}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 110,
      render: (date) => {
        const dueDate = new Date(date);
        const isOverdue = dueDate < new Date();
        return (
          <Text type={isOverdue ? 'danger' : undefined}>
            {dueDate.toLocaleDateString()}
          </Text>
        );
      }
    },
    {
      title: 'Paid At',
      dataIndex: 'paidAt',
      key: 'paidAt',
      width: 110,
      render: (date) => date ? new Date(date).toLocaleDateString() : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            title="View Details"
          />
          {record.status === 'PENDING' && (
            <Popconfirm
              title="Mark this invoice as paid?"
              onConfirm={() => handleMarkPaid(record)}
            >
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                style={{ color: '#52c41a' }}
                title="Mark Paid"
              />
            </Popconfirm>
          )}
          {record.status === 'PAID' && (
            <Popconfirm
              title="Refund this invoice?"
              description="This action cannot be undone."
              onConfirm={() => handleRefund(record)}
            >
              <Button
                type="text"
                icon={<RollbackOutlined />}
                danger
                title="Refund"
              />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  // Handle view invoice details (mobile)
  const handleMobileViewDetails = (invoice: AdminInvoice) => {
    setSelectedInvoice(invoice);
    setDetailDrawerVisible(true);
  };

  // Mobile card configuration
  const mobileCardConfig = {
    primaryField: 'invoiceNo' as const,
    secondaryField: 'companyName' as const,
    statusField: 'status' as const,
    statusConfig: {
      PAID: { color: 'green', text: 'Paid' },
      PENDING: { color: 'orange', text: 'Pending' },
      OVERDUE: { color: 'red', text: 'Overdue' },
      FAILED: { color: 'red', text: 'Failed' },
      REFUNDED: { color: 'purple', text: 'Refunded' },
    },
    avatar: {
      icon: <FileTextOutlined />,
    },
    fields: [
      {
        key: 'total' as const,
        label: 'Total',
        render: (v: number, record: AdminInvoice) => (
          <Text strong>${Number(v).toLocaleString()} {record.currency}</Text>
        ),
      },
      {
        key: 'plan' as const,
        label: 'Plan',
        render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '-',
      },
      {
        key: 'dueDate' as const,
        label: 'Due Date',
        render: (v: string) => {
          const dueDate = new Date(v);
          const isOverdue = dueDate < new Date();
          return (
            <Text type={isOverdue ? 'danger' : undefined}>
              {dueDate.toLocaleDateString()}
            </Text>
          );
        },
      },
    ],
    onClick: handleMobileViewDetails,
  };

  // Mobile filters
  const mobileFilters = [
    {
      key: 'status',
      placeholder: 'Status',
      options: [
        { label: 'Pending', value: 'PENDING' },
        { label: 'Paid', value: 'PAID' },
        { label: 'Overdue', value: 'OVERDUE' },
        { label: 'Failed', value: 'FAILED' },
        { label: 'Refunded', value: 'REFUNDED' },
      ],
      value: filterStatus,
      onChange: (v: string) => {
        setFilterStatus(v);
        fetchInvoices({ page: 1 });
      },
    },
    {
      key: 'company',
      placeholder: 'Company',
      options: companies.map(c => ({ label: c.name, value: c.id })),
      value: filterCompany,
      onChange: (v: string) => {
        setFilterCompany(v);
        fetchInvoices({ page: 1 });
      },
    },
  ];

  // Mobile view
  if (isMobile) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <FileTextOutlined /> Invoices
          </Title>
        </div>

        <MobileDataList
          data={invoices}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={loading}
          emptyText="No invoices found"
          onRefresh={() => fetchInvoices()}
          filters={mobileFilters}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page, pageSize) => fetchInvoices({ page, limit: pageSize }),
          }}
          swipeActions={{
            right: {
              icon: <EyeOutlined />,
              color: '#1890ff',
              label: 'View',
              onClick: handleMobileViewDetails,
            },
          }}
        />

        {/* Invoice Detail Drawer (Mobile) */}
        <Drawer
          title={`Invoice ${selectedInvoice?.invoiceNo}`}
          placement="bottom"
          height="85%"
          open={detailDrawerVisible}
          onClose={() => setDetailDrawerVisible(false)}
        >
          {selectedInvoice && (
            <div>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Card size="small" title="Invoice Info">
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div>
                      <Text type="secondary">Invoice No</Text>
                      <div><Text strong>{selectedInvoice.invoiceNo}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Company</Text>
                      <div><Text>{selectedInvoice.companyName}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Company ID</Text>
                      <div><Text>{selectedInvoice.companyCode}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Plan</Text>
                      <div>
                        {selectedInvoice.plan ? (
                          <Tag color="blue">{selectedInvoice.plan}</Tag>
                        ) : '-'}
                      </div>
                    </div>
                    <div>
                      <Text type="secondary">Status</Text>
                      <div>
                        <Tag color={getStatusColor(selectedInvoice.status)}>
                          {selectedInvoice.status}
                        </Tag>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card size="small" title="Amount">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <Text type="secondary">Amount</Text>
                      <div><Text strong>${Number(selectedInvoice.amount).toLocaleString()}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Tax</Text>
                      <div><Text strong>${Number(selectedInvoice.tax).toLocaleString()}</Text></div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Text type="secondary">Total</Text>
                      <div>
                        <Text strong style={{ fontSize: 18 }}>
                          ${Number(selectedInvoice.total).toLocaleString()} {selectedInvoice.currency}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card size="small" title="Dates">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <Text type="secondary">Period Start</Text>
                      <div><Text>{new Date(selectedInvoice.periodStart).toLocaleDateString()}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Period End</Text>
                      <div><Text>{new Date(selectedInvoice.periodEnd).toLocaleDateString()}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Due Date</Text>
                      <div><Text>{new Date(selectedInvoice.dueDate).toLocaleDateString()}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Paid At</Text>
                      <div>
                        <Text>
                          {selectedInvoice.paidAt
                            ? new Date(selectedInvoice.paidAt).toLocaleDateString()
                            : '-'}
                        </Text>
                      </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Text type="secondary">Created At</Text>
                      <div><Text>{new Date(selectedInvoice.createdAt).toLocaleString()}</Text></div>
                    </div>
                  </div>
                </Card>

                <Space direction="vertical" style={{ width: '100%' }}>
                  {selectedInvoice.status === 'PENDING' && (
                    <Popconfirm
                      title="Mark this invoice as paid?"
                      onConfirm={() => {
                        handleMarkPaid(selectedInvoice);
                        setDetailDrawerVisible(false);
                      }}
                    >
                      <Button block type="primary" icon={<CheckCircleOutlined />}>
                        Mark as Paid
                      </Button>
                    </Popconfirm>
                  )}
                  {selectedInvoice.status === 'PAID' && (
                    <Popconfirm
                      title="Refund this invoice?"
                      description="This action cannot be undone."
                      onConfirm={() => {
                        handleRefund(selectedInvoice);
                        setDetailDrawerVisible(false);
                      }}
                    >
                      <Button block danger icon={<RollbackOutlined />}>
                        Refund Invoice
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </Space>
            </div>
          )}
        </Drawer>
      </div>
    );
  }

  // Desktop view
  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <FileTextOutlined /> Invoice Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Status"
              style={{ width: 130 }}
              allowClear
              value={filterStatus}
              onChange={(v) => {
                setFilterStatus(v);
                fetchInvoices({ page: 1 });
              }}
            >
              <Option value="PENDING">Pending</Option>
              <Option value="PAID">Paid</Option>
              <Option value="OVERDUE">Overdue</Option>
              <Option value="FAILED">Failed</Option>
              <Option value="REFUNDED">Refunded</Option>
            </Select>
            <Select
              placeholder="Company"
              style={{ width: 180 }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={filterCompany}
              onChange={(v) => {
                setFilterCompany(v);
                fetchInvoices({ page: 1 });
              }}
            >
              {companies.map(c => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
            <RangePicker
              onChange={(dates) => {
                setDateRange(dates as any);
                fetchInvoices({ page: 1 });
              }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchInvoices()}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} invoices`
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Invoice Detail Modal */}
      <Modal
        title={`Invoice Details: ${selectedInvoice?.invoiceNo}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedInvoice && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Invoice No" span={2}>
              {selectedInvoice.invoiceNo}
            </Descriptions.Item>
            <Descriptions.Item label="Company">
              {selectedInvoice.companyName}
            </Descriptions.Item>
            <Descriptions.Item label="Company ID">
              {selectedInvoice.companyCode}
            </Descriptions.Item>
            <Descriptions.Item label="Plan">
              {selectedInvoice.plan || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedInvoice.status)}>
                {selectedInvoice.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              ${Number(selectedInvoice.amount).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Tax">
              ${Number(selectedInvoice.tax).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Total" span={2}>
              <Text strong style={{ fontSize: 16 }}>
                ${Number(selectedInvoice.total).toLocaleString()} {selectedInvoice.currency}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Period Start">
              {new Date(selectedInvoice.periodStart).toLocaleDateString()}
            </Descriptions.Item>
            <Descriptions.Item label="Period End">
              {new Date(selectedInvoice.periodEnd).toLocaleDateString()}
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {new Date(selectedInvoice.dueDate).toLocaleDateString()}
            </Descriptions.Item>
            <Descriptions.Item label="Paid At">
              {selectedInvoice.paidAt
                ? new Date(selectedInvoice.paidAt).toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At" span={2}>
              {new Date(selectedInvoice.createdAt).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceManagement;
